import { useEffect, useRef, useState } from 'react'
import { Editor, posToDOMRect } from '@tiptap/react'
import { 
  TextIcon,
  Heading1,
  Heading2,
  Heading3,
  ListOrdered,
  List,
  CheckSquare,
  Table,
  Image,
  Code,
} from 'lucide-react'

interface CommandMenuProps {
  editor: Editor
}

interface CommandItem {
  title: string
  description: string
  icon: React.ReactNode
  command: (editor: Editor) => void
}

const CommandMenu: React.FC<CommandMenuProps> = ({ editor }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const [query, setQuery] = useState('')
  const menuRef = useRef<HTMLDivElement>(null)

  const commands: CommandItem[] = [
    {
      title: '文本',
      description: '普通文本段落',
      icon: <TextIcon className="w-4 h-4" />,
      command: (editor) => {
        editor.chain().focus().setParagraph().run()
      }
    },
    {
      title: '一级标题',
      description: '大标题',
      icon: <Heading1 className="w-4 h-4" />,
      command: (editor) => {
        editor.chain().focus().toggleHeading({ level: 1 }).run()
      }
    },
    {
      title: '二级标题',
      description: '中标题',
      icon: <Heading2 className="w-4 h-4" />,
      command: (editor) => {
        editor.chain().focus().toggleHeading({ level: 2 }).run()
      }
    },
    {
      title: '三级标题',
      description: '小标题',
      icon: <Heading3 className="w-4 h-4" />,
      command: (editor) => {
        editor.chain().focus().toggleHeading({ level: 3 }).run()
      }
    },
    {
      title: '无序列表',
      description: '项目符号列表',
      icon: <List className="w-4 h-4" />,
      command: (editor) => {
        editor.chain().focus().toggleBulletList().run()
      }
    },
    {
      title: '有序列表',
      description: '数字列表',
      icon: <ListOrdered className="w-4 h-4" />,
      command: (editor) => {
        editor.chain().focus().toggleOrderedList().run()
      }
    },
    {
      title: '任务列表',
      description: '待办事项列表',
      icon: <CheckSquare className="w-4 h-4" />,
      command: (editor) => {
        editor.chain().focus().toggleTaskList().run()
      }
    },
    {
      title: '代码块',
      description: '代码块，支持语法高亮',
      icon: <Code className="w-4 h-4" />,
      command: (editor) => {
        editor.chain().focus().toggleCodeBlock().run()
      }
    },
    {
      title: '表格',
      description: '插入表格',
      icon: <Table className="w-4 h-4" />,
      command: (editor) => {
        editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
      }
    },
    {
      title: '图片',
      description: '插入图片',
      icon: <Image className="w-4 h-4" />,
      command: (editor) => {
        const url = window.prompt('输入图片 URL')
        if (url) {
          editor.chain().focus().setImage({ src: url }).run()
        }
      }
    },
  ]

  const filteredCommands = commands.filter((command) => 
    command.title.toLowerCase().includes(query.toLowerCase()) || 
    command.description.toLowerCase().includes(query.toLowerCase())
  )

  const handleSlash = () => {
    if (!editor) return false;

    const { state } = editor
    const { selection } = state
    const { empty, anchor } = selection

    if (!empty || !editor.isActive('paragraph')) {
      return false
    }

    const currentLine = state.doc.textBetween(
      selection.$anchor.start(),
      selection.$anchor.pos
    )

    if (currentLine === '/') {
      if (anchor && editor.view && editor.view.dom) {
        const pos = anchor
        const domRect = posToDOMRect(editor.view, pos, pos)
        setPosition({ x: domRect.left, y: domRect.bottom })
        setIsOpen(true)
        setQuery('')
        
        editor.chain().deleteRange({
          from: selection.$anchor.start(),
          to: selection.$anchor.pos
        }).run()
        
        return true
      }
    }
    
    return false
  }

  useEffect(() => {
    if (editor) {
      editor.setOptions({
        editorProps: {
          handleKeyDown: (_view, event) => {
            if (event.key === '/') {
              return handleSlash();
            }
            return false;
          }
        }
      });
    }

    return () => {
    }
  }, [editor]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSelectCommand = (command: CommandItem) => {
    command.command(editor)
    setIsOpen(false)
  }

  if (!isOpen) {
    return null
  }

  return (
    <div 
      ref={menuRef}
      className="absolute z-50 bg-white rounded-md shadow-lg border border-gray-200 w-64"
      style={{ top: position.y, left: position.x }}
    >
      <div className="p-2 text-sm text-gray-500 border-b">
        输入命令或使用箭头键选择
      </div>
      <div className="max-h-60 overflow-y-auto">
        {filteredCommands.length > 0 ? (
          filteredCommands.map((command, index) => (
            <button
              key={index}
              className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              onClick={() => handleSelectCommand(command)}
            >
              <div className="p-1 bg-gray-100 rounded">{command.icon}</div>
              <div>
                <div className="font-medium">{command.title}</div>
                <div className="text-xs text-gray-500">{command.description}</div>
              </div>
            </button>
          ))
        ) : (
          <div className="p-3 text-center text-gray-500">没有匹配的命令</div>
        )}
      </div>
    </div>
  )
}

export default CommandMenu 